<template>
    <div id="edit-folder" v-mdl class="mdl-card mdl-shadow--6dp">
        <div class="mdl-card__title">
            <h2 class="mdl-card__title-text">
                {{ $t('folders.edit') }}
            </h2>
        </div>

        <!-- Rendered once the folder is loaded, so v-mdl upgrades the field with its value in place. -->
        <div v-if="folder" v-mdl class="mdl-card__supporting-text">
            <div class="mdl-textfield mdl-js-textfield">
                <input id="folder-name" v-model="name" class="mdl-textfield__input" maxlength="100" @keyup.enter="save">
                <label class="mdl-textfield__label" for="folder-name">{{ $t('folders.name') }}</label>
            </div>

            <!-- PULS-1424: Android's edit folder dialog lists every conversation with a checkbox; each
                 tap adds it to or removes it from the folder straight away. -->
            <h4 class="conversations-heading">
                {{ $t('folders.conversations') }}
            </h4>
            <input v-if="conversations.length > FILTER_THRESHOLD" v-model="filter" class="conversation-filter" :placeholder="$t('folders.filter')">
            <p v-if="conversationsLoading" class="conversations-status">
                {{ $t('folders.loading_conversations') }}
            </p>
            <p v-else-if="conversations.length === 0" class="conversations-status">
                {{ $t('folders.no_conversations') }}
            </p>
            <ul v-else class="conversation-list">
                <li v-for="conversation in filteredConversations" :key="conversation.device_id" :class="{ disabled: inOtherFolder(conversation) }">
                    <label>
                        <input type="checkbox" :checked="inThisFolder(conversation)" :disabled="inOtherFolder(conversation) || pending.includes(conversation.device_id)" @change="toggleConversation(conversation, $event.target.checked)">
                        <span class="conversation-title">{{ conversation.titleNoEmoji }}</span>
                        <span v-if="inOtherFolder(conversation)" class="conversation-note">{{ $t('folders.in_other_folder') }}</span>
                    </label>
                </li>
            </ul>
        </div>

        <div class="mdl-card__actions mdl-card--border">
            <button id="save" class="mdl-button mdl-js-button mdl-js-ripple-effect" :disabled="!folder" @click="save">
                {{ $t('dialog.save') }}
            </button>
            <button id="cancel" class="mdl-button mdl-js-button mdl-js-ripple-effect" @click="goToFolders">
                {{ $t('dialog.cancel') }}
            </button>
            <button id="delete" class="delete-button mdl-button mdl-js-button mdl-js-ripple-effect" :disabled="!folder" @click="deleteFolder">
                {{ $t('dialog.delete') }}
            </button>
        </div>

        <transition name="loading-fade">
            <div v-if="loading" class="loading-center">
                <spinner />
            </div>
        </transition>
    </div>
</template>

<script>
import { Api, Util, SessionCache } from '@/utils/';
import Spinner from '@/components/Spinner.vue';

// Show a filter box above the conversation list when it's longer than this.
const FILTER_THRESHOLD = 10;

// PULS-1424: Android's edit folder dialog (FolderUtils.promptEditFolder): rename, choose the folder's
// conversations, or delete behind a confirmation.
export default {
    name: 'EditFolder',
    components: {
        Spinner
    },
    props: ['folderId'],
    data () {
        return {
            folder: null,
            name: "",
            loading: true,
            conversations: [],
            conversationsLoading: true,
            pending: [],
            filter: "",
            FILTER_THRESHOLD,
        };
    },
    computed: {
        filteredConversations () {
            const filter = this.filter.trim().toLowerCase();
            if (!filter) {
                return this.conversations;
            }

            return this.conversations.filter(conversation =>
                conversation.titleNoEmoji.toLowerCase().indexOf(filter) !== -1 ||
                (conversation.phone_numbers || '').indexOf(filter) !== -1);
        }
    },
    mounted () {
        this.$store.state.msgbus.$on('conversationFolderChanged', this.folderChangedElsewhere);
        this.$store.commit("loading", false);
        this.$store.commit('title', this.$t('folders.edit'));

        Api.folders.get()
            .then(folders => {
                const folder = folders.find(f => String(f.device_id) === String(this.folderId));
                if (!folder) {
                    this.goToFolders();
                    return;
                }

                this.name = folder.name || "";
                this.folder = folder;
                this.loadConversations();
            })
            .catch(() => this.goToFolders())
            .finally(() => {
                this.loading = false;
            });
    },
    beforeDestroy () {
        this.$store.state.msgbus.$off('conversationFolderChanged', this.folderChangedElsewhere);
    },
    methods: {
        // A push (or this screen's own request) moved conversations between folders or deleted a
        // folder. Keep the checkboxes and "in another folder" locks in step, so a conversation the
        // phone just put in another folder can't be ticked here and silently moved.
        folderChangedElsewhere ({ ids, folderId, removed }) {
            if (removed) {
                if (this.folder && String(folderId) === String(this.folder.device_id)) {
                    this.goToFolders();
                    return;
                }

                for (const conversation of this.conversations) {
                    if (String(conversation.folder_id) === String(folderId)) {
                        conversation.folder_id = -1;
                    }
                }
                return;
            }

            const changed = (ids || []).map(String);
            for (const conversation of this.conversations) {
                if (changed.indexOf(String(conversation.device_id)) !== -1) {
                    conversation.folder_id = folderId;
                }
            }
        },
        // Private conversations are left out: Android doesn't allow them in folders, and listing their
        // titles here would show them without the passcode.
        loadConversations () {
            Api.conversations.getAll()
                .then(conversations => {
                    this.conversations = conversations.filter(conversation => !conversation.private_notifications);
                })
                .catch(() => {
                    Util.snackbar(this.$t('folders.load_conversations_failed'));
                })
                .finally(() => {
                    this.conversationsLoading = false;
                });
        },
        inThisFolder (conversation) {
            return String(conversation.folder_id) === String(this.folder.device_id);
        },
        // As Android: a conversation can only be in one folder, so ones in another folder can't be ticked.
        inOtherFolder (conversation) {
            return conversation.folder_id !== undefined && conversation.folder_id !== null && Number(conversation.folder_id) !== -1 && !this.inThisFolder(conversation);
        },
        toggleConversation (conversation, checked) {
            const previous = conversation.folder_id;
            const id = conversation.device_id;
            conversation.folder_id = checked ? this.folder.device_id : -1;
            this.pending.push(id);

            const request = checked
                ? Api.conversations.addToFolder(id, this.folder.device_id)
                : Api.conversations.removeFromFolder(id);
            request
                .catch(() => {
                    conversation.folder_id = previous;
                    Util.snackbar(this.$t('folders.assign_failed', { title: conversation.titleNoEmoji }));
                })
                .finally(() => {
                    this.pending = this.pending.filter(pendingId => pendingId !== id);
                });
        },
        goToFolders () {
            this.$router.push({ name: "folders" });
        },
        save () {
            const name = this.name.trim();
            if (!name) {
                Util.snackbar(this.$t('folders.name_required'));
                return;
            }

            if (name === this.folder.name) {
                this.goToFolders();
                return;
            }

            this.loading = true;
            Api.folders.update(this.folder.device_id, name)
                .then(() => {
                    Util.snackbar(this.$t('folders.updated'));
                    this.goToFolders();
                })
                .catch(() => {
                    Util.snackbar(this.$t('folders.update_failed'));
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        deleteFolder () {
            const options = {
                okText: this.$t('dialog.delete'),
                cancelText: this.$t('dialog.cancel'),
                animation: 'fade'
            };

            this.$dialog.confirm(this.$t('conversations.deletefolderconfirm'), options)
                .then(() => {
                    this.loading = true;
                    const id = this.folder.device_id;
                    return Api.folders.delete(id)
                        .then(() => {
                            // The server moves its conversations out of the folder; mirror that now
                            // rather than waiting for the removed_folder push.
                            SessionCache.removeFolder(id);
                            Util.snackbar(this.$t('folders.deleted', { name: this.folder.name }));
                            this.goToFolders();
                        })
                        .catch(() => {
                            Util.snackbar(this.$t('folders.delete_failed'));
                        })
                        .finally(() => {
                            this.loading = false;
                        });
                })
                .catch(() => { }); // Cancelled
        }
    }
};
</script>

<style lang="scss" scoped>
    #edit-folder {
        position: relative;
        width: 400px;
        max-width: calc(100% - 32px);
        height: 100%;
        margin: 5em auto;
    }

    .delete-button {
        float: right;
    }

    .mdl-textfield {
        width: 100%;
    }

    .conversations-heading {
        margin: 8px 0;
        font-size: 16px;
        font-weight: 500;
    }

    .conversation-filter {
        width: 100%;
        box-sizing: border-box;
        margin-bottom: 8px;
        padding: 6px 8px;
        font: inherit;
        border: 1px solid rgba(0, 0, 0, 0.12);
        border-radius: 4px;
        background: transparent;
        color: inherit;
    }

    .conversations-status {
        margin: 8px 0;
    }

    .conversation-list {
        max-height: 50vh;
        overflow-y: auto;
        margin: 0;
        padding: 0;
        list-style: none;

        li.disabled {
            opacity: 0.4;
        }

        label {
            display: flex;
            align-items: center;
            padding: 6px 0;
            cursor: pointer;
        }

        li.disabled label {
            cursor: default;
        }

        input {
            flex: none;
            margin: 0 12px 0 0;
        }

        .conversation-title {
            flex: 1;
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .conversation-note {
            flex: none;
            margin-left: 8px;
            font-size: 12px;
        }
    }

    .loading-center {
        display: flex;
        justify-content: center;
        align-items: center;
        position: absolute;
        margin: 57px auto 0;
        background: #fff;
        height: calc(100% - 57px);
        width: 100%;
    }
</style>
